/**
 * Mock Profiles for Testing
 * 
 * Simulates different user roles so the real backend RBAC pipeline
 * determines which agents are visible. No hardcoded agent lists —
 * the system works the same way it does in production.
 * 
 * When a mock profile is active, the frontend sends the profile's
 * roles to the Express backend via the x-mock-profile-roles header.
 * The backend's agentAccessControl middleware runs calculateAgentsFromRoles()
 * with those roles, returning exactly the agents that role set would see.
 * 
 * Usage:
 * - Toggle in browser console: window.askAldo.toggleMockProfile('default')
 * - Or use the testing panel in the UI (when enabled)
 * - Profiles persist in localStorage until disabled
 */

export interface MockProfile {
  id: string;
  name: string;
  description: string;
  roles: string[];
  isAdmin: boolean;
}

// Predefined mock profiles for testing
// Roles must match ENTRA_ROLE_AGENT_MAP keys in agentAccessControl.js.
// The backend's RBAC logic (ENTRA_DEFAULT_ALLOWED_AGENTS + ENTRA_ROLE_AGENT_MAP)
// determines which agents each role set can see — not this file.
export const MOCK_PROFILES: Record<string, MockProfile> = {
  'default': {
    id: 'default',
    name: 'Default User',
    description: 'Standard employee — sees only default agents (via ENTRA_DEFAULT_ALLOWED_AGENTS)',
    roles: ['authenticated'],
    isAdmin: false
  },
  'sales': {
    id: 'sales',
    name: 'Sales Rep',
    description: 'product-user role — defaults + Product Searchlight',
    roles: ['authenticated', 'product-user'],
    isAdmin: false
  },
  'embroidery': {
    id: 'embroidery',
    name: 'Embroidery Operator',
    description: 'owensboro2-user role — defaults + Direct Embroidery',
    roles: ['authenticated', 'owensboro2-user'],
    isAdmin: false
  },
  'pwms': {
    id: 'pwms',
    name: 'PWMS User',
    description: 'pwms-user role — defaults + PWMS Support',
    roles: ['authenticated', 'pwms-user'],
    isAdmin: false
  },
  'iris': {
    id: 'iris',
    name: 'IRIS User',
    description: 'iris-user role — defaults + IRIS',
    roles: ['authenticated', 'iris-user'],
    isAdmin: false
  },
  'power_user': {
    id: 'power_user',
    name: 'Power User',
    description: 'Multiple specialist roles — sees all gated agents',
    roles: ['authenticated', 'product-user', 'pwms-user', 'iris-user', 'owensboro2-user', 'firstaid-safety-user', 'erp-user'],
    isAdmin: false
  },
  'admin': {
    id: 'admin',
    name: 'Admin / Developer',
    description: 'super-admin role — full access to all agents + admin dashboard',
    roles: ['authenticated', 'admin', 'super-admin', 'product-admin', 'hr-admin', 'pwms-admin', 'aldo-admin', 'orientation-admin', 'iris-admin'],
    isAdmin: true
  }
};

// LocalStorage key for active mock profile
const MOCK_PROFILE_KEY = 'askaldo_mock_profile';
const MOCK_PROFILE_ENABLED_KEY = 'askaldo_mock_profile_enabled';

/**
 * Get the current mock profile (if enabled)
 */
export const getActiveMockProfile = (): MockProfile | null => {
  try {
    const enabled = localStorage.getItem(MOCK_PROFILE_ENABLED_KEY) === 'true';
    if (!enabled) return null;

    const profileId = localStorage.getItem(MOCK_PROFILE_KEY);
    if (!profileId || !MOCK_PROFILES[profileId]) return null;

    return MOCK_PROFILES[profileId];
  } catch {
    return null;
  }
};

/**
 * Enable a mock profile by ID
 */
export const enableMockProfile = (profileId: string): boolean => {
  try {
    if (!MOCK_PROFILES[profileId]) {
      console.error(`Mock profile '${profileId}' not found. Available: ${Object.keys(MOCK_PROFILES).join(', ')}`);
      return false;
    }

    localStorage.setItem(MOCK_PROFILE_KEY, profileId);
    localStorage.setItem(MOCK_PROFILE_ENABLED_KEY, 'true');
    
    console.log(`✅ Mock profile enabled: ${MOCK_PROFILES[profileId].name}`);
    console.log(`   Roles: ${MOCK_PROFILES[profileId].roles.join(', ')}`);
    console.log(`   Refresh the page to apply.`);
    
    return true;
  } catch (error) {
    console.error('Failed to enable mock profile:', error);
    return false;
  }
};

/**
 * Disable mock profiles (return to normal behavior)
 */
export const disableMockProfile = (): void => {
  try {
    localStorage.removeItem(MOCK_PROFILE_ENABLED_KEY);
    localStorage.removeItem(MOCK_PROFILE_KEY);
    console.log('✅ Mock profile disabled. Refresh the page to restore normal agent access.');
  } catch (error) {
    console.error('Failed to disable mock profile:', error);
  }
};

/**
 * Toggle mock profile on/off
 */
export const toggleMockProfile = (profileId: string): boolean => {
  const currentProfile = getActiveMockProfile();
  
  if (currentProfile?.id === profileId) {
    disableMockProfile();
    return false;
  } else {
    enableMockProfile(profileId);
    return true;
  }
};

/**
 * Check if mock profiles are currently enabled
 */
export const isMockProfileEnabled = (): boolean => {
  try {
    return localStorage.getItem(MOCK_PROFILE_ENABLED_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Get the mock profile's roles for backend RBAC.
 * Returns null when no profile is active.
 */
export const getMockProfileRoles = (): string[] | null => {
  const profile = getActiveMockProfile();
  if (!profile) return null;
  return profile.roles;
};

/**
 * Get all available mock profiles
 */
export const getAvailableProfiles = (): MockProfile[] => {
  return Object.values(MOCK_PROFILES);
};

// Helper to check if running on localhost
const isLocalhost = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || 
         hostname === '127.0.0.1' || 
         hostname.startsWith('192.168.') ||
         hostname.endsWith('.local');
};

// Expose to window for console access - ONLY on localhost
if (typeof window !== 'undefined' && isLocalhost()) {
  (window as any).askAldo = (window as any).askAldo || {};
  (window as any).askAldo.mockProfiles = {
    list: () => {
      console.table(Object.values(MOCK_PROFILES).map(p => ({
        id: p.id,
        name: p.name,
        description: p.description,
        roles: p.roles.filter(r => r !== 'authenticated').join(', ') || '(defaults only)'
      })));
    },
    enable: enableMockProfile,
    disable: disableMockProfile,
    toggle: toggleMockProfile,
    current: getActiveMockProfile,
    isEnabled: isMockProfileEnabled
  };
  
  // Log help on load
  console.log('🧪 Ask Aldo Mock Profiles available (localhost only). Use:');
  console.log('   window.askAldo.mockProfiles.list()     - Show all profiles');
  console.log('   window.askAldo.mockProfiles.enable("default") - Enable a profile');
  console.log('   window.askAldo.mockProfiles.disable()  - Disable mock profile');
  console.log('   window.askAldo.mockProfiles.current()  - Show current profile');
}

export default {
  MOCK_PROFILES,
  getActiveMockProfile,
  enableMockProfile,
  disableMockProfile,
  toggleMockProfile,
  isMockProfileEnabled,
  getMockProfileRoles,
  getAvailableProfiles
};

